import styled, { CSSProperties, css } from 'styled-components';

type ColWidth = number | string | 'auto';

const gapSizes = {
  none: '0',
  sm: '8px',
  md: '16px',
  lg: '32px',
  xl: '48px',
} as const;

interface Props {
  cols: ColWidth[];
  middle?: boolean;
  gapSize?: keyof typeof gapSizes;
  columnGapSize?: keyof typeof gapSizes;
  clickable?: boolean;
  position?: CSSProperties['position'];
  justifyItems?: CSSProperties['justifyItems'];
  rows?: ColWidth[];
}

const prepareColumnDefinitions = (def: Props, type: 'cols' | 'rows' = 'cols'): string => {
  const addUnits = (v: ColWidth) => (typeof v === 'string' ? v : `${v}fr`);

  const values = type === 'cols' ? def.cols : def.rows;

  return values ? values.map(addUnits).join(' ') : '';
};

// Doesn't do much, but it signifies in the component code what goes together as a column
// so you don't have to think about 'what does this stray div do'
export const Column = styled.div<{ align?: 'left' | 'center' | 'right'; valign?: 'top' | 'center' | 'bottom' }>`
  ${(props) =>
    props.align &&
    css`
      text-align: ${props.align};
    `};

  ${(props) =>
    props.valign &&
    css`
      align-items: ${props.valign};
    `};
`;

export const Columns = styled.div<Props>`
  display: grid;
  grid-template-columns: ${(props) => prepareColumnDefinitions(props, 'cols')};
  gap: ${(props) => gapSizes[props.gapSize || 'md']};
  ${(props) => props.columnGapSize && `grid-column-gap: ${gapSizes[props.columnGapSize]};`};
  grid-template-rows: ${(props) => prepareColumnDefinitions(props, 'rows')};

  ${(props) =>
    props.justifyItems &&
    css`
      justify-items: ${props.justifyItems};
    `};

  ${(props) =>
    props.position &&
    css`
      position: ${props.position};
    `};

  ${(props) =>
    props.middle &&
    css`
      align-items: center;
    `};

  ${(props) =>
    props.clickable &&
    css`
      cursor: pointer;
    `};
`;
